import { useI18N } from "@sundae/react-hooks";
import { Balance, FlexList, FlexListItem, Text } from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { memo, useMemo } from "react";

import LoadingComponent from "../../../../components/LoadingComponent";
import Countdown from "../../../../components/YieldFarmingComponents/EarningsCountdown";
import { ADA_METADATA } from "../../../../constants/cardano.constants";
import { useRollupQuery } from "../../../../gql/hooks/farms.query";
import { useAdaData } from "../../../../hooks/useAdaData";
import NoDataText from "../../../../i18n/components/warnings/NoDataText";
import { joyrideSelectors } from "../../../../constants/joyride.constants";

const GeneralStats = () => {
  const { t, currentLocale } = useI18N("farms");
  const { data: rollupData, isLoading: isLoadingRollupData } = useRollupQuery();
  const { data: adaData } = useAdaData();

  const averageDailyRewards = useMemo(() => {
    const days = rollupData?.numDays;
    const emittedEst = rollupData?.emittedEstLovelace;

    if (!days || !emittedEst) return null;

    const amount = Math.round(emittedEst / days);
    return new AssetAmount(amount, ADA_METADATA.decimals);
  }, [rollupData?.emittedEstLovelace, rollupData?.numDays]);

  const tvl = useMemo(() => {
    const days = rollupData?.numDays;
    const lockedEst = rollupData?.lockedEstLovelace;

    if (!days || !lockedEst) return null;

    const amount = Math.round(lockedEst / days);
    return new AssetAmount(amount, ADA_METADATA.decimals);
  }, [rollupData?.lockedEstLovelace, rollupData?.numDays]);

  const tvlComponent = useMemo(() => {
    if (tvl) {
      return (
        <Balance
          abbreviate={false}
          locale={currentLocale}
          showSymbol
          size="sm"
          usdValue={(adaData?.usd ?? 0) * tvl.value.toNumber()}
          value={tvl}
        />
      );
    }

    return <NoDataText />;
  }, [tvl]);

  const averageDailyRewardsComponent = useMemo(() => {
    if (averageDailyRewards) {
      return (
        <Balance
          abbreviate={false}
          locale={currentLocale}
          showSymbol
          size="sm"
          usdValue={(adaData?.usd ?? 0) * averageDailyRewards.value.toNumber()}
          value={averageDailyRewards}
        />
      );
    }

    return <NoDataText />;
  }, [averageDailyRewards]);

  if (isLoadingRollupData) {
    return (
      <div className="flex min-h-[8rem] items-center justify-center">
        <LoadingComponent />
      </div>
    );
  }

  return (
    <div
      className="grid h-fit gap-6"
      data-guide-selector={joyrideSelectors.guides.yieldFarming.stats}
    >
      <Text tag="div" weight="semibold">
        {t("landingPage.generalStatsTitle")}
      </Text>
      <FlexList>
        <FlexListItem
          field={{ label: t("tvl", { ns: "glossary" }), size: "sm" }}
          tooltip={t("landingPage.tvlTooltip")}
          value={{
            label: tvlComponent,
          }}
        />
        <FlexListItem
          field={{ label: t("landingPage.averageDailyRewards"), size: "sm" }}
          value={{
            label: averageDailyRewardsComponent,
          }}
        />
        <FlexListItem
          field={{ label: t("nextRewardsCycle"), size: "sm" }}
          value={{
            label: <Countdown size="sm" withProgress={false} />,
          }}
        />
      </FlexList>
    </div>
  );
};

export default memo(GeneralStats);
